/**
 * axios 实例（架构 §2 src/api/client.ts / T09）
 *  - baseURL: 生产用 import.meta.env.VITE_API_BASE（Worker 独立域名）；dev 回退 '/api/v1' 经 Vite 代理
 *  - 请求拦截：注入 Authorization: Bearer <JWT> 与 x-locale（U2 locale 透传）
 *  - 响应拦截：统一解析 { code, data, message } 信封
 *      - code === 0 -> 返回 data
 *      - 否则抛出 ApiError（携带 code/message）
 *      - 1001 / 1002 -> 清 token 并触发 authFailHandler（跳登录）
 */
import axios, { AxiosError } from 'axios';
import type { ApiEnvelope } from '../types/models';
import { ErrorCode } from '../types/models';
import { useAuthStore } from '../store/auth.store';

/** API 错误（携带后端错误码） */
export class ApiError extends Error {
  code: number;
  data: unknown;
  constructor(code: number, message: string, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.data = data;
  }
}

/** 鉴权失败时由 App 注册的跳转钩子（避免 client 直接依赖 react-router） */
let authFailHandler: (() => void) | null = null;
export function setAuthFailHandler(fn: (() => void) | null) {
  authFailHandler = fn;
}

/** 当前 locale（由 i18n/LocaleProvider 写入，随每个 REST 请求头透传后端） */
let apiLocale = 'zh';
export function setApiLocale(locale: string) {
  apiLocale = locale === 'en' ? 'en' : 'zh';
}

// 规范化 baseURL：保证以 '/api/v1' 结尾（兼容 dev 回退 '/api/v1' 与 prod 注入的 worker 根 URL）
function resolveBaseURL(): string {
  const raw = (import.meta.env.VITE_API_BASE || '/api/v1').trim().replace(/\/+$/, '');
  return raw.endsWith('/api/v1') ? raw : `${raw}/api/v1`;
}

const instance = axios.create({
  baseURL: resolveBaseURL(),
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// 请求拦截：注入 Bearer + x-locale
instance.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // U2：locale 透传（后端 memory/weekly/AI 提示据此产出语言）
  config.headers['x-locale'] = apiLocale;
  return config;
});

function handleUnauthorized() {
  useAuthStore.getState().logout();
  if (authFailHandler) authFailHandler();
}

// 响应拦截：解析信封
instance.interceptors.response.use(
  (resp) => {
    const body = resp.data as ApiEnvelope<unknown>;
    if (body && typeof body.code === 'number') {
      if (body.code === ErrorCode.OK) {
        return body.data as unknown;
      }
      if (body.code === ErrorCode.AUTH_INVALID_TOKEN || body.code === ErrorCode.AUTH_EXPIRED) {
        handleUnauthorized();
      }
      throw new ApiError(body.code, body.message || '请求失败', body.data);
    }
    // 非信封响应：原样返回
    return resp.data;
  },
  (error: AxiosError<ApiEnvelope<unknown>>) => {
    const body = error.response?.data;
    if (body && typeof body.code === 'number') {
      if (body.code === ErrorCode.AUTH_INVALID_TOKEN || body.code === ErrorCode.AUTH_EXPIRED) {
        handleUnauthorized();
      }
      throw new ApiError(body.code, body.message || '请求失败', body.data);
    }
    throw new ApiError(ErrorCode.SERVER_ERROR, error.message || '网络错误', null);
  },
);

/** 强类型 GET：直接返回 data 部分 */
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  return (await instance.get(url, { params })) as unknown as T;
}

export async function post<T>(url: string, data?: unknown): Promise<T> {
  return (await instance.post(url, data)) as unknown as T;
}

export async function patch<T>(url: string, data?: unknown): Promise<T> {
  return (await instance.patch(url, data)) as unknown as T;
}

export async function del<T>(url: string, data?: unknown): Promise<T> {
  return (await instance.delete(url, { data })) as unknown as T;
}

export default instance;
